import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Send, Sparkles, Loader2, GitBranch } from "lucide-react";
import RouteSimulation from "@/components/marketing/RouteSimulation";

const SEED_BY_SECTOR = {
  mortgages: [
    "Hi, I'd like to remortgage my flat in Manchester.",
    "First-time buyer, looking at £350k in London.",
    "I'm self-employed — do I qualify for a BTL mortgage?",
  ],
  "debt-help": [
    "I'm worried about debt — credit card and overdraft.",
    "I have £24k across 5 cards and I'm behind on payments.",
    "Can you tell me about an IVA?",
  ],
  "business-finance": [
    "I run a small business and need £40k working capital.",
    "Looking at asset finance for warehouse equipment.",
    "Need a bridging loan £1.2M for a development site.",
  ],
  "life-insurance": [
    "Looking for a life insurance quote for my family.",
    "I have two young kids and a £280k mortgage.",
    "Self-employed, 38, non-smoker — what cover do I need?",
  ],
  "claims-management": [
    "I think I was mis-sold PCP finance on my car.",
    "My landlord won't fix damp — can I claim?",
    "Have I got a housing-disrepair claim?",
  ],
};

const SID_KEY = "connexis_chat_sid";
const SECTOR_KEY = "connexis_chat_sector";

const WELCOMES = {
  mortgages:          "Hello, you're through to Connexis AI. I help with mortgages — purchase, remortgage, BTL. What's the situation?",
  "debt-help":        "Hi, you're through to Connexis AI debt-support. Everything you share stays confidential. What's worrying you most right now?",
  "business-finance": "Hello, you're through to Connexis AI for business finance — working capital, asset finance, bridging. What does the business need?",
  "life-insurance":   "Hi, you're through to Connexis AI for life insurance. I'll ask a few questions to find the right cover. Who's the policy for?",
  "claims-management":"Hi, you're through to Connexis AI claims. Tell me briefly what happened and I'll check whether you have a route to claim.",
};
const welcomeFor = (s) => ({ role: "assistant", text: WELCOMES[s] || WELCOMES.mortgages });

const newSessionId = () =>
  `web-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;

export default function AiChatDemo() {
  const [sector, setSector] = useState(() => {
    if (typeof window === "undefined") return "mortgages";
    return localStorage.getItem(SECTOR_KEY) || "mortgages";
  });
  const [sessionId, setSessionId] = useState(() => {
    const existing = typeof window !== "undefined" ? localStorage.getItem(SID_KEY) : null;
    if (existing) return existing;
    const sid = newSessionId();
    try { localStorage.setItem(SID_KEY, sid); } catch {}
    return sid;
  });
  const [messages, setMessages] = useState([welcomeFor(sector)]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [qualifying, setQualifying] = useState(false);
  const [qualification, setQualification] = useState(null);
  const scrollRef = useRef(null);

  // Restore history on mount
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/ai/chat/${sessionId}`);
        if (cancel) return;
        if (data?.messages?.length) {
          const restoredSector = data.sector || sector;
          setMessages([welcomeFor(restoredSector), ...data.messages.map((m) => ({ role: m.role, text: m.text }))]);
          if (data.sector) setSector(data.sector);
        }
      } catch {}
    })();
    return () => { cancel = true; };
  // eslint-disable-next-line
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const userTurns = messages.filter((m) => m.role === "user").length;
  const canQualify = userTurns >= 2 && !busy && !qualifying;

  // Change sector → start a fresh session anchored to that sector.
  // (Backend persists sector per session, so without a new session the AI keeps
  //  the original sector context — that's the bug the user spotted.)
  const handleSectorChange = (newSector) => {
    if (newSector === sector) return;
    const hadConversation = userTurns > 0;
    if (hadConversation) {
      const ok = window.confirm("Switching sector will start a fresh conversation. Continue?");
      if (!ok) return;
    }
    const sid = newSessionId();
    try {
      localStorage.setItem(SID_KEY, sid);
      localStorage.setItem(SECTOR_KEY, newSector);
    } catch {}
    setSessionId(sid);
    setSector(newSector);
    setMessages([welcomeFor(newSector)]);
    setQualification(null);
    setError(null);
  };

  const send = async (text) => {
    if (!text.trim() || busy) return;
    setError(null);
    const next = [...messages, { role: "user", text }];
    setMessages(next);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: sessionId, message: text, sector });
      setMessages([...next, { role: "assistant", text: data.assistant }]);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "AI service is busy. Try again.");
      setMessages(next);
    } finally {
      setBusy(false);
    }
  };

  const qualify = async () => {
    if (!canQualify) return;
    setQualifying(true);
    setError(null);
    try {
      const { data } = await api.post(`/ai/chat/${sessionId}/qualify`);
      setQualification(data);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Could not route this enquiry yet — answer a couple more questions.");
    } finally {
      setQualifying(false);
    }
  };

  const reset = () => {
    const sid = newSessionId();
    try { localStorage.setItem(SID_KEY, sid); } catch {}
    setSessionId(sid);
    setMessages([welcomeFor(sector)]);
    setQualification(null);
    setError(null);
    setInput("");
  };

  return (
    <>
      <div className="rounded-2xl border border-slate-200 bg-white shadow-float overflow-hidden" data-testid="ai-chat-demo">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <span className="inline-grid place-items-center size-8 rounded-md bg-primary/10 text-primary"><Sparkles className="size-4" /></span>
            <div className="leading-tight">
              <div className="text-sm font-medium text-slate-900">Connexis AI · live demo</div>
              <div className="text-[11px] text-slate-500">Connexis AI · FCA-aware script</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={sector} onValueChange={handleSectorChange}>
              <SelectTrigger className="w-36 h-8 text-xs" data-testid="ai-chat-sector"><SelectValue placeholder="Sector" /></SelectTrigger>
              <SelectContent>
                {[["mortgages", "Mortgages"], ["debt-help", "Debt Help"], ["business-finance", "Business Finance"], ["life-insurance", "Life Insurance"], ["claims-management", "Claims"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <button onClick={reset} className="text-[11px] text-slate-500 hover:text-slate-900" data-testid="ai-chat-reset" title="Start over">Reset</button>
          </div>
        </div>

        <div ref={scrollRef} className="h-[360px] overflow-y-auto px-5 py-4 space-y-3 bg-slate-50/40">
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role !== "user" && <span className="shrink-0 mt-0.5 inline-flex h-6 w-12 items-center justify-center rounded-md text-[10px] font-semibold bg-primary/10 text-primary">AI</span>}
              <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                {m.text.split("\n").map((line, k) => <p key={k} className={k > 0 ? "mt-2" : ""}>{line}</p>)}
              </div>
              {m.role === "user" && <span className="shrink-0 mt-0.5 inline-flex h-6 w-12 items-center justify-center rounded-md text-[10px] font-semibold bg-slate-100 text-slate-600">YOU</span>}
            </div>
          ))}
          {busy && (
            <div className="flex gap-2.5">
              <span className="shrink-0 mt-0.5 inline-flex h-6 w-12 items-center justify-center rounded-md text-[10px] font-semibold bg-primary/10 text-primary">AI</span>
              <div className="rounded-2xl px-3.5 py-2.5 bg-white border border-slate-200 text-slate-500 text-sm inline-flex items-center gap-2"><Loader2 className="size-3.5 animate-spin" /> Thinking…</div>
            </div>
          )}
          {error && <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>}
        </div>

        {canQualify && (
          <div className="px-3 pt-3 bg-white border-t border-slate-100">
            <button
              onClick={qualify}
              disabled={qualifying}
              data-testid="ai-chat-qualify"
              className="w-full group inline-flex items-center justify-between gap-3 rounded-xl bg-gradient-to-r from-slate-900 to-slate-800 text-white px-4 py-3 hover:from-slate-800 hover:to-slate-700 transition-all"
            >
              <span className="flex items-center gap-2.5">
                <span className="inline-grid place-items-center size-7 rounded-md bg-primary text-white"><GitBranch className="size-3.5" /></span>
                <span className="text-left leading-tight">
                  <span className="block text-sm font-medium">See how this enquiry would route</span>
                  <span className="block text-[11px] text-slate-300">Live ping-tree + buyer bids in &lt;1s</span>
                </span>
              </span>
              {qualifying ? <Loader2 className="size-4 animate-spin" /> : <span className="text-xs font-mono text-slate-400 group-hover:text-white">→</span>}
            </button>
          </div>
        )}

        <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="px-3 py-3 border-t border-slate-200 bg-white">
          {messages.length <= 1 && (
            <div className="px-2 mb-2 flex flex-wrap gap-1.5">
              {(SEED_BY_SECTOR[sector] || SEED_BY_SECTOR.mortgages).map((s, i) => (
                <button key={i} type="button" onClick={() => send(s)} disabled={busy} className="text-xs rounded-full border border-slate-200 bg-white hover:bg-slate-50 px-3 py-1 text-slate-700" data-testid={`ai-chat-seed-${i}`}>
                  {s}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your enquiry…"
              disabled={busy}
              className="flex-1"
              data-testid="ai-chat-input"
            />
            <Button type="submit" disabled={busy || !input.trim()} className="rounded-full px-4 h-9" data-testid="ai-chat-send">
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
            </Button>
          </div>
        </form>
      </div>

      {qualification && <RouteSimulation data={qualification} sessionId={sessionId} onClose={() => setQualification(null)} />}
    </>
  );
}
